// =============================================
// Service access — the role ∩ plan intersection
// =============================================
// A user can actually USE a service only when BOTH gates pass:
//   1. their ROLE allows it      (user_rights.status = 'Yes')   — what the role may touch
//   2. their PLAN includes it    (site_service_plan_access.access_level != 'none') — what's paid for
//
// This module is the single source of truth for that intersection. It powers the
// Roles & Permissions → Workbench "Preview" view (which greys out impossible combos)
// and is the primitive real enforcement code should call, e.g.
//   if (!canRoleAccessServiceOnPlan(roleTypeId, planId, serviceId, name)) hide/deny.
//
// NOTE: the schema does not model a direct user→plan link for general (non-client)
// services yet, so callers pass the planId explicitly. Wire a real lookup here once
// users carry a plan_id.
// =============================================
import { getTableData } from '../features/db-viewer/data/dataAccess';

export type AccessLevel = 'none' | 'read' | 'limited' | 'full';
export type EffectiveStatus = 'allowed' | 'blocked-by-plan' | 'blocked-by-role' | 'off';

export const EFFECTIVE_LABEL: Record<EffectiveStatus, string> = {
  allowed: 'Allowed',
  'blocked-by-plan': 'Blocked by plan',
  'blocked-by-role': 'Blocked by role',
  off: 'Off',
};

/**
 * Pure classifier — the only place the four-state truth table lives.
 * Keep UI and enforcement consistent by routing both through this.
 */
export function classifyAccess(roleAllowed: boolean, planLevel: AccessLevel): EffectiveStatus {
  const inPlan = planLevel !== 'none';
  if (roleAllowed && inPlan) return 'allowed';
  if (roleAllowed && !inPlan) return 'blocked-by-plan';
  if (!roleAllowed && inPlan) return 'blocked-by-role';
  return 'off';
}

/**
 * Does the role allow this service? Matches user_rights by service_id (exact, after
 * migration 057) with a service_name fallback for rows not yet backfilled.
 * Pass `rights` to classify against in-memory state; omit to read the local cache.
 */
export function roleAllowsService(
  roleTypeId: number,
  serviceId: number,
  serviceName: string,
  rights?: any[],
): boolean {
  const rows = rights ?? getTableData('user_rights');
  const row = rows.find((r: any) =>
    // eslint-disable-next-line eqeqeq
    r.user_type_id == roleTypeId && (
      // eslint-disable-next-line eqeqeq
      (r.service_id != null && r.service_id == serviceId) ||
      (r.service_id == null && (r.service_name || '').trim().toLowerCase() === serviceName.trim().toLowerCase())
    ));
  return row?.status === 'Yes';
}

/** The access level a plan grants for a service ('none' when not included). */
export function planLevelForService(planId: number, serviceId: number, access?: any[]): AccessLevel {
  const rows = access ?? getTableData('site_service_plan_access');
  const row = rows.find((r: any) =>
    // eslint-disable-next-line eqeqeq
    r.is_deleted != 1 && r.plan_id == planId && r.service_id == serviceId);
  return (row?.access_level as AccessLevel) || 'none';
}

export interface EffectiveAccess {
  status: EffectiveStatus;
  roleAllowed: boolean;
  planLevel: AccessLevel;
}

/** Full intersection result for one (role, plan, service). */
export function effectiveServiceStatus(
  roleTypeId: number,
  planId: number,
  serviceId: number,
  serviceName: string,
  data?: { rights?: any[]; access?: any[] },
): EffectiveAccess {
  const roleAllowed = roleAllowsService(roleTypeId, serviceId, serviceName, data?.rights);
  const planLevel = planLevelForService(planId, serviceId, data?.access);
  return { status: classifyAccess(roleAllowed, planLevel), roleAllowed, planLevel };
}

/** The enforcement primitive: true only when role AND plan both permit the service. */
export function canRoleAccessServiceOnPlan(
  roleTypeId: number,
  planId: number,
  serviceId: number,
  serviceName: string,
  data?: { rights?: any[]; access?: any[] },
): boolean {
  return effectiveServiceStatus(roleTypeId, planId, serviceId, serviceName, data).status === 'allowed';
}

// =============================================
// Name resolution + plan gating (for nav, which keys off service NAMES)
// =============================================
// The nav registry maps items to user_rights-style service names ('Accounting',
// 'Cloud Drive (like Prism)', …). These don't all match site_services.service_name,
// so resolve via explicit overrides first, then a case-insensitive name match.
const NAME_TO_CODE: Record<string, string> = {
  'cloud drive (like prism)': 'CLOUD',
  'movie links': 'MOVIES',
};

/** site_services.service_id for a nav/user_rights service name, or null if unknown. */
export function resolveServiceIdByName(serviceName: string, services?: any[]): number | null {
  const rows = services ?? getTableData('site_services');
  const key = serviceName.trim().toLowerCase();
  const code = NAME_TO_CODE[key];
  if (code) {
    const byCode = rows.find((s: any) => (s.service_code || '').toUpperCase() === code && s.is_deleted != 1); // eslint-disable-line eqeqeq
    if (byCode) return byCode.service_id;
  }
  // Prefer a top-level match, then any match.
  const top = rows.find((s: any) => s.is_deleted != 1 && s.parent_service_id == null && (s.service_name || '').trim().toLowerCase() === key); // eslint-disable-line eqeqeq
  if (top) return top.service_id;
  const any = rows.find((s: any) => s.is_deleted != 1 && (s.service_name || '').trim().toLowerCase() === key); // eslint-disable-line eqeqeq
  return any ? any.service_id : null;
}

/** Does the plan have ANY access rows at all? Used to fail open when data is absent. */
export function planHasAnyAccessRows(planId: number, access?: any[]): boolean {
  const rows = access ?? getTableData('site_service_plan_access');
  return rows.some((r: any) => r.is_deleted != 1 && r.plan_id == planId); // eslint-disable-line eqeqeq
}

export type PlanNavDecision = 'include' | 'exclude' | 'unknown';

/**
 * Should a plan show a service (by name)? Tri-state so callers can FAIL OPEN:
 *   'include'  — plan covers it (or the service has a covered ancestor)
 *   'exclude'  — plan has access data but this service isn't in it
 *   'unknown'  — can't tell (no plan, unresolved service, or no access data) → don't gate
 */
export function planServiceDecisionByName(
  planId: number | null | undefined,
  serviceName: string,
  data?: { services?: any[]; access?: any[] },
): PlanNavDecision {
  if (planId == null) return 'unknown';
  const serviceId = resolveServiceIdByName(serviceName, data?.services);
  if (serviceId == null) return 'unknown';
  if (!planHasAnyAccessRows(planId, data?.access)) return 'unknown';
  // A child service counts as included if it or any ancestor is included
  // (granting a parent implies the sub-modules below it).
  const services = data?.services ?? getTableData('site_services');
  let cur: any = services.find((s: any) => s.service_id == serviceId); // eslint-disable-line eqeqeq
  let guard = 0;
  while (cur && guard++ < 20) {
    if (planLevelForService(planId, cur.service_id, data?.access) !== 'none') return 'include';
    const parentId = cur.parent_service_id;
    if (parentId == null) break;
    cur = services.find((s: any) => s.service_id == parentId); // eslint-disable-line eqeqeq
  }
  return 'exclude';
}

// =============================================
// Per-user grants (the entitlement layer on top of role ∩ plan)
// =============================================
const LEVEL_RANK: Record<AccessLevel, number> = { none: 0, read: 1, limited: 2, full: 3 };

function todayStr(): string { return new Date().toISOString().slice(0, 10); }

/** Is a grant row live right now? (not deleted/inactive, status active, within window) */
export function isGrantActive(g: any, today: string = todayStr()): boolean {
  if (!g) return false;
  if (g.is_deleted == 1 || g.is_active == 0) return false;          // eslint-disable-line eqeqeq
  if (g.status && g.status !== 'active') return false;
  if (g.valid_from && String(g.valid_from).slice(0, 10) > today) return false;
  if (g.valid_until && String(g.valid_until).slice(0, 10) < today) return false;
  return true;
}

/** The active grant a user holds for a service, or null. */
export function activeGrantFor(userId: number, serviceId: number, grants?: any[], today: string = todayStr()): any | null {
  const rows = grants ?? getTableData('user_service_grant');
  return rows.find((g: any) => g.user_id == userId && g.service_id == serviceId && isGrantActive(g, today)) || null; // eslint-disable-line eqeqeq
}

export interface UserServiceAccess extends EffectiveAccess {
  source: 'grant' | 'plan' | 'none';
  grant: any | null;
}

/**
 * A user's effective access to a service = role ∩ (plan ∨ active grant).
 * A grant can lift access above the plan (e.g. plan excludes it but a trial grant
 * includes it). `source` says which side won; `grant` carries the row (for expiry).
 */
export function userEffectiveAccess(
  roleTypeId: number,
  userId: number,
  planId: number | null | undefined,
  serviceId: number,
  serviceName: string,
  data?: { rights?: any[]; access?: any[]; grants?: any[] },
): UserServiceAccess {
  const roleAllowed = roleAllowsService(roleTypeId, serviceId, serviceName, data?.rights);
  const planLevel: AccessLevel = planId == null ? 'none' : planLevelForService(planId, serviceId, data?.access);
  const g = activeGrantFor(userId, serviceId, data?.grants);
  const grantLevel: AccessLevel = g ? ((g.access_level as AccessLevel) || 'full') : 'none';
  let level: AccessLevel = planLevel;
  let source: UserServiceAccess['source'] = planLevel === 'none' ? 'none' : 'plan';
  if (LEVEL_RANK[grantLevel] > LEVEL_RANK[planLevel]) { level = grantLevel; source = 'grant'; }
  return { status: classifyAccess(roleAllowed, level), roleAllowed, planLevel: level, source, grant: source === 'grant' ? g : null };
}

/**
 * Nav decision for a real user: plan OR an active grant can include the service.
 * Tri-state so the caller can fail open (see App.tsx layer 2.5).
 */
export function userServiceNavDecision(
  userId: number,
  planId: number | null | undefined,
  serviceName: string,
  data?: { services?: any[]; access?: any[]; grants?: any[] },
): PlanNavDecision {
  const serviceId = resolveServiceIdByName(serviceName, data?.services);
  if (serviceId != null) {
    const g = activeGrantFor(userId, serviceId, data?.grants);
    if (g && ((g.access_level as AccessLevel) || 'full') !== 'none') return 'include';
  }
  return planServiceDecisionByName(planId, serviceName, data);
}
